import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronDown, ArrowLeft, Check, Loader2, Star, PartyPopper, Gift } from 'lucide-react';
import apiService from '../services/api';
import { useAuth } from '../context/AuthContext';
import { SubscriptionTier } from '../services/subscriptionApi';
import { TierSelectorCards } from './subscription';
import DeploymentProgressModal from './DeploymentProgressModal';
import { useToast } from '../context/ToastContext';
import { useSoftLaunch } from '../hooks';
import { usePasswordPolicy } from '../hooks/usePasswordPolicy';
import PasswordRequirements from './common/PasswordRequirements';
import { useMyCampaign } from '../hooks/useMyCampaign';

interface CreateWorkspaceModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

interface BillingProfile {
  id: number;
  name: string;
  company_name: string | null;
  is_default: boolean;
}

const CreateWorkspaceModal: React.FC<CreateWorkspaceModalProps> = ({ open, onClose, onCreated }) => {
const { user, serverRegion, agencyId } = useAuth();
  const { showSuccess, showError } = useToast();
  const { isEnabled: isSoftLaunch, trialDays, messaging: softLaunchMessaging } = useSoftLaunch();
const myCampaign = useMyCampaign();

  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  // Form state
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [newWorkspaceSlug, setNewWorkspaceSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [showSlugEditor, setShowSlugEditor] = useState(false);
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier | null>(null);
  const [creating, setCreating] = useState(false);
const [validationError, setValidationError] = useState<string | null>(null);
  const [useMyInfo, setUseMyInfo] = useState(true);
  const [notifyCustomerAdmin, setNotifyCustomerAdmin] = useState(false);
  const [emailReadiness, setEmailReadiness] = useState<'none' | 'partial' | 'ready' | null>(null);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminFirstName, setAdminFirstName] = useState('');
  const [adminLastName, setAdminLastName] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [validatingPassword, setValidatingPassword] = useState(false);
  const [passwordValidationError, setPasswordValidationError] = useState<string | null>(null);
const { policy: passwordPolicy } = usePasswordPolicy();

  const [billingProfiles, setBillingProfiles] = useState<BillingProfile[]>([]);
  const [selectedBillingProfileId, setSelectedBillingProfileId] = useState<number | ''>('');
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [billingDropdownOpen, setBillingDropdownOpen] = useState(false);

  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'unavailable'>('idle');
  const [slugError, setSlugError] = useState<string | null>(null);

  // Access code state
  const [accessCode, setAccessCode] = useState('');
  const [accessCodeValidated, setAccessCodeValidated] = useState(false);
  const [accessCodeError, setAccessCodeError] = useState<string | null>(null);
  const [unlockedTier, setUnlockedTier] = useState<SubscriptionTier | null>(null);
  const [showAccessCodeInput, setShowAccessCodeInput] = useState(false);

  const [deploymentModalOpen, setDeploymentModalOpen] = useState(false);
  const [deployingWorkspaceId, setDeployingWorkspaceId] = useState<number | null>(null);
  const [deployingWorkspaceName, setDeployingWorkspaceName] = useState<string>('');

  // Hover states
  const [cancelHovered, setCancelHovered] = useState(false);
  const [backHovered, setBackHovered] = useState(false);
  const [nextHovered, setNextHovered] = useState(false);

  useEffect(() => {
    if (open) {
      loadBillingProfiles();
      resetForm();
      checkPendingAccessCode();
    }
  }, [open]);

  const checkPendingAccessCode = async () => {
    try {
      const response = await fetch('/api/tier-access/pending', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      const data = await response.json();
      if (data.has_pending && data.access_code) {
        setAccessCode(data.access_code.code);
        setAccessCodeValidated(true);
        setUnlockedTier(data.access_code.tier);
        setSelectedTier(data.access_code.tier);
        setShowAccessCodeInput(true);
      }
    } catch (err) {
      console.error('Failed to check pending access code:', err);
    }
  };

  const validateAccessCode = async (code: string) => {
    if (!code.trim()) {
      setAccessCodeValidated(false);
      setAccessCodeError(null);
      setUnlockedTier(null);
      return;
    }

    try {
      const response = await fetch('/api/tier-access/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.toUpperCase() }),
      });
      const data = await response.json();
      
      if (data.valid && data.accessCode) {
        setAccessCodeValidated(true);
        setAccessCodeError(null);
        setUnlockedTier(data.accessCode.tier);
        setSelectedTier(data.accessCode.tier);
      } else {
        setAccessCodeValidated(false);
        setAccessCodeError(data.error || 'Invalid access code');
        setUnlockedTier(null);
      }
    } catch (err) {
      setAccessCodeError('Failed to validate code');
      setAccessCodeValidated(false);
    }
  };

  const handleAccessCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setAccessCode(value);
    setAccessCodeValidated(false);
    setAccessCodeError(null);
  };

  const handleAccessCodeApply = () => {
    if (accessCode.trim()) {
      validateAccessCode(accessCode);
    }
  };

// Populate admin fields from `user` whenever useMyInfo is true.
  // Re-fires when user loads from AuthContext. Does NOT touch adminPassword
  // (so AuthContext re-renders don't wipe what the user typed).
useEffect(() => {
    if (useMyInfo && user) {
      setAdminEmail(user.email);
      const nameParts = user.name.trim().split(/\s+/);
      setAdminFirstName(nameParts[0] || user.name);
      setAdminLastName(nameParts.slice(1).join(' ') || '');
    }
  }, [useMyInfo, user]);

  // Clear admin fields only when the user actively unchecks "Use my info".
  // Tracks previous useMyInfo via ref so this only fires on a true→false
  // transition, not on initial mount or on user-ref changes.
  const prevUseMyInfoRef = React.useRef(useMyInfo);
  useEffect(() => {
    if (prevUseMyInfoRef.current && !useMyInfo) {
      setAdminEmail('');
      setAdminFirstName('');
      setAdminLastName('');
      setAdminPassword('');
    }
    prevUseMyInfoRef.current = useMyInfo;
  }, [useMyInfo]);

  useEffect(() => {
    if (!useMyInfo && agencyId) {
      apiService.get(`/agencies/${agencyId}/email-readiness`)
        .then((r: any) => setEmailReadiness(r.data.status))
        .catch(() => setEmailReadiness('none'));
    }
    if (useMyInfo) {
      setNotifyCustomerAdmin(false);
      setEmailReadiness(null);
    }
  }, [useMyInfo, agencyId]);

  const loadBillingProfiles = async () => {
    try {
      setLoadingProfiles(true);
      const response = await apiService.get('/billing/profiles/names');
      const profiles = response.data?.profiles || response.data || [];
      setBillingProfiles(profiles);

const defaultProfile = profiles.find((p: BillingProfile) => p.is_default);
      if (defaultProfile) {
        setSelectedBillingProfileId(defaultProfile.id);
      } else if (profiles.length >= 1) {
        setSelectedBillingProfileId(profiles[0].id);
      }
    } catch (error) {
      console.error('Failed to load billing profiles:', error);
      setBillingProfiles([]);
    } finally {
      setLoadingProfiles(false);
    }
  };

  const checkSlugAvailability = useCallback(async (slug: string) => {
    if (!slug || slug.length < 4) {
      setSlugStatus('idle');
      setSlugError(slug.length > 0 && slug.length < 4 ? 'Slug must be at least 4 characters' : null);
      return;
    }

const slugRegex = /^[a-z]([a-z0-9-]*[a-z0-9])?$/;
    if (!slugRegex.test(slug) || slug.includes('--')) {
      setSlugStatus('unavailable');
      setSlugError('Must start with a letter; only lowercase letters, numbers, and hyphens allowed');
      return;
    }

    setSlugStatus('checking');
    setSlugError(null);

    try {
      const response = await apiService.get(`/workspaces/check-slug/${encodeURIComponent(slug)}`);
      if (response.data.available) {
        setSlugStatus('available');
        setSlugError(null);
      } else {
        setSlugStatus('unavailable');
        setSlugError(response.data.error || 'This slug is unavailable');
      }
    } catch (err) {
      setSlugStatus('idle');
      setSlugError(null);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      if (newWorkspaceSlug) {
        checkSlugAvailability(newWorkspaceSlug);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [newWorkspaceSlug, checkSlugAvailability, open]);

const resetForm = () => {
    setCurrentStep(1);
    setNewWorkspaceName('');
    setNewWorkspaceSlug('');
    setSlugManuallyEdited(false);
    setShowSlugEditor(false);
    setSelectedTier(null);
    setAdminPassword('');
    setPasswordValidationError(null);
    setValidatingPassword(false);
    setCreating(false);
    setSlugStatus('idle');
    setSlugError(null);
    setSelectedBillingProfileId('');
    setValidationError(null);
    setUseMyInfo(true);
    setAccessCode('');
    setAccessCodeValidated(false);
    setAccessCodeError(null);
    setUnlockedTier(null);
    setShowAccessCodeInput(false);
  };

  const generateSlug = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 50);
  };

  const handleWorkspaceNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewWorkspaceName(value);
    if (!slugManuallyEdited) {
      setNewWorkspaceSlug(generateSlug(value));
    }
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSlugManuallyEdited(true);
    const value = e.target.value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .substring(0, 50);
    setNewWorkspaceSlug(value);
  };

  const validatePasswordBeforeCreation = async (): Promise<boolean> => {
    if (useMyInfo) {
      setValidatingPassword(true);
      setPasswordValidationError(null);
      try {
        const response = await fetch('/api/auth/password/validate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
          body: JSON.stringify({ password: adminPassword }),
        });
        if (!response.ok) {
          setPasswordValidationError('Incorrect password');
          return false;
        }
        return true;
} catch (error) {
        setPasswordValidationError('Failed to validate password');
        return false;
      } finally {
        setValidatingPassword(false);
      }
    }

    // Setting a NEW admin password — run the full server-side policy before
    // creating anything. The browser can only check length; breach and
    // name/email screening are server-side. Catching it here means no
    // workspace or deployment job is created for a password the pod would
    // later reject.
    setValidatingPassword(true);
    setPasswordValidationError(null);
    try {
      const res = await fetch('/api/auth/check-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: adminPassword,
          email: adminEmail,
          name: `${adminFirstName} ${adminLastName}`.trim(),
          workspaceName: newWorkspaceName,
        }),
      });
      const data = await res.json();
      if (data && data.valid === false) {
        setPasswordValidationError(data.errors?.[0] || 'Please choose a different password');
        return false;
      }
    } catch {
      // Fail open — the backend enforces the policy on create regardless.
    } finally {
      setValidatingPassword(false);
    }

    return true;
  };

  const isStepValid = (step: number): boolean => {
    if (step === 1) {
      if (!newWorkspaceName.trim()) return false;
      if (!newWorkspaceSlug.trim() || newWorkspaceSlug.length < 4) return false;
      if (slugStatus === 'checking' || slugStatus === 'unavailable') return false;
      return true;
    }
if (step === 2) {
      return isSoftLaunch || !!selectedTier;
    }
    if (step === 3) {
if (!isSoftLaunch && !selectedBillingProfileId) return false;
if (!adminEmail?.trim() || !adminFirstName?.trim() || !adminLastName?.trim()) return false;
if (!adminPassword || (!useMyInfo && Array.from(adminPassword).length < passwordPolicy.minLength)) return false;
      return true;
    }
    return true;
  };

  const validateStep = (step: number): boolean => {
    setValidationError(null);

    if (step === 1) {
      if (!newWorkspaceName.trim()) {
        setValidationError('Please enter a workspace name');
        return false;
      }
      if (!newWorkspaceSlug.trim() || newWorkspaceSlug.length < 4) {
        setValidationError('Workspace URL must be at least 4 characters');
        return false;
      }
      if (slugStatus === 'checking') {
        setValidationError('Please wait while we verify URL availability');
        return false;
      }
      if (slugStatus === 'unavailable') {
        setValidationError(slugError || 'Please choose a different workspace URL');
        return false;
      }
      return true;
    }

if (step === 2) {
      if (!isSoftLaunch && !selectedTier) {
        setValidationError('Please select a subscription plan');
        return false;
      }
      return true;
    }

    if (step === 3) {
if (!isSoftLaunch && !selectedBillingProfileId) {
        setValidationError('Please select a billing account');
        return false;
      }
if (!adminEmail?.trim()) {
        setValidationError('Please enter admin login or email');
        return false;
      }
if (!adminFirstName?.trim()) {
        setValidationError('Please enter admin first name');
        return false;
      }
if (!adminLastName?.trim()) {
        setValidationError('Please enter admin last name');
        return false;
      }
      if (!adminPassword) {
        setValidationError('Please enter admin password');
        return false;
      }
if (!useMyInfo && Array.from(adminPassword).length < passwordPolicy.minLength) {
        setValidationError(`Password must be at least ${passwordPolicy.minLength} characters`);
        return false;
      }
      return true;
    }

    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    setValidationError(null);
  };

  const handleCreate = async () => {
    if (!validateStep(3)) return;

    const isPasswordValid = await validatePasswordBeforeCreation();
    if (!isPasswordValid) return;

    try {
      setCreating(true);
      setValidationError(null);

const workspaceData = {
        name: newWorkspaceName,
        domain: newWorkspaceSlug,
	tier_id: isSoftLaunch ? 1 : selectedTier?.id,
        subscription_tier: isSoftLaunch ? 'trial_growth' : selectedTier?.tier_code,
        soft_launch: isSoftLaunch,
        customer_admin_email: adminEmail,
        customer_admin_first_name: adminFirstName,
        customer_admin_last_name: adminLastName,
customer_admin_password: adminPassword,
        billing_profile_id: selectedBillingProfileId,
        notify_customer_admin: notifyCustomerAdmin,
      };

      const response = await apiService.createWorkspace(workspaceData);

      setDeployingWorkspaceId(response.id);
      setDeployingWorkspaceName(response.name);
      setDeploymentModalOpen(true);
    } catch (error: any) {
      showError(error.message || 'Failed to create workspace');
    } finally {
      setCreating(false);
    }
  };

  const handleDeploymentComplete = () => {
    setDeploymentModalOpen(false);
    showSuccess('Workspace created successfully');
    onCreated();
    onClose();
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = 'var(--v2-primary)';
    e.target.style.boxShadow = '0 0 0 3px var(--v2-primary-muted)';
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = 'var(--v2-border)';
    e.target.style.boxShadow = 'none';
  };

  const selectedBillingProfile = billingProfiles.find((p) => p.id === selectedBillingProfileId);

  if (deploymentModalOpen && deployingWorkspaceId) {
    return (
      <DeploymentProgressModal
        open={deploymentModalOpen}
        workspaceId={deployingWorkspaceId}
        workspaceName={deployingWorkspaceName}
        onComplete={handleDeploymentComplete}
      />
    );
  }

  if (!open) return null;

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'var(--v2-surface)',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '0.875rem',
    color: 'var(--v2-text)',
    outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: 'var(--v2-text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '8px',
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '0.9375rem',
    fontWeight: 700,
    color: 'var(--v2-text)',
    margin: '0 0 20px 0',
  };

  const helperTextStyle: React.CSSProperties = {
    fontSize: '0.8125rem',
    color: 'var(--v2-text-tertiary)',
    margin: '6px 0 0 0',
  };

  const canProceed = isStepValid(currentStep);
  const canCreate = !creating && !validatingPassword && isStepValid(3) && (isSoftLaunch || billingProfiles.length > 0);



  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          style={{
            backgroundColor: 'var(--v2-surface)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-lg)',
            border: '1px solid var(--v2-border)',
            maxWidth: '1050px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: 'var(--font-sans)',
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (billingDropdownOpen) setBillingDropdownOpen(false);
          }}
        >
          {/* Header with Progress */}
          <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--v2-border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--v2-text)', margin: 0 }}>
                Create New Workspace
              </h2>
              <span style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', fontWeight: 500 }}>
                Step {currentStep} of {totalSteps}
              </span>
            </div>
            <div
              style={{
                height: '4px',
                backgroundColor: 'var(--v2-border)',
                borderRadius: 'var(--radius-full)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${(currentStep / totalSteps) * 100}%`,
                  backgroundColor: 'var(--v2-primary)',
                  borderRadius: 'var(--radius-full)',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </div>

          {/* Content */}
          <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
            {validationError && (
              <div
                style={{
                  backgroundColor: 'var(--v2-error-bg)',
                  border: '1px solid #fca5a5',
                  borderRadius: 'var(--radius-md)',
                  padding: '12px 16px',
                  marginBottom: '20px',
                }}
              >
                <p style={{ fontSize: '0.875rem', color: 'var(--v2-error)', margin: 0 }}>{validationError}</p>
              </div>
            )}

            {/* STEP 1: Workspace Basics */}
            {currentStep === 1 && (
              <div>
                <p style={sectionTitleStyle}>Workspace Basics</p>

                <div style={{ marginBottom: '24px' }}>
                  <label style={labelStyle}>Workspace Name *</label>
                  <input
                    type="text"
                    value={newWorkspaceName}
                    onChange={handleWorkspaceNameChange}
                    placeholder="e.g., Acme Corporation"
                    style={inputStyle}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    autoFocus
                  />
                  <p style={helperTextStyle}>Your company or organization name</p>
                </div>

                {/* Inline Slug Preview */}
                <div
                  style={{
                    backgroundColor: 'var(--v2-surface-alt)',
                    border: '1px solid var(--v2-border)',
                    borderRadius: 'var(--radius-md)',
                    padding: '14px 16px',
                    marginBottom: '24px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: showSlugEditor ? '12px' : 0,
                    }}
                  >
                    <div>
                      <p
                        style={{
                          fontSize: '0.6875rem',
                          fontWeight: 600,
                          color: 'var(--v2-text-muted)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          margin: '0 0 4px 0',
                        }}
                      >
                        Your Workspace URL
                      </p>
                      <p
                        style={{
                          fontSize: '0.9375rem',
                          fontWeight: 600,
                          color:
                            slugStatus === 'unavailable'
                              ? 'var(--v2-error)'
                              : slugStatus === 'available'
                                ? 'var(--v2-success)'
                                : 'var(--v2-text)',
                          margin: 0,
                          fontFamily: 'var(--font-mono)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        {newWorkspaceSlug || 'your-slug'}.dashboardfox.app
                        {slugStatus === 'checking' && (
                          <Loader2 size={14} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                        )}
                        {slugStatus === 'available' && <Check size={14} strokeWidth={2} color="var(--v2-success)" />}
                        {slugStatus === 'unavailable' && <X size={14} strokeWidth={2} color="var(--v2-error)" />}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowSlugEditor(!showSlugEditor)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--v2-primary)',
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                        padding: '4px 8px',
                      }}
>
                        {showSlugEditor ? 'Done' : 'Edit'}
                      </button>
                    </div>

                    {!showSlugEditor && (
                      newWorkspaceSlug.length > 0 && newWorkspaceSlug.length < 4 ? (
                        <p style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)', margin: '8px 0 0 0' }}>
                          Minimum 4 characters
                        </p>
                      ) : slugError ? (
                        <p style={{ fontSize: '0.8125rem', color: 'var(--v2-error)', margin: '8px 0 0 0' }}>
                          {slugError}
                        </p>
                      ) : null
                    )}

                    {showSlugEditor && (
                    <div>
                      <input
                        type="text"
                        value={newWorkspaceSlug}
                        onChange={handleSlugChange}
                        placeholder="acme-corp"
                        style={{
                          ...inputStyle,
                          fontFamily: 'var(--font-mono)',
                          borderColor:
                            slugStatus === 'unavailable'
                              ? 'var(--v2-error)'
                              : slugStatus === 'available'
                                ? 'var(--v2-success)'
                                : 'var(--v2-border)',
                        }}
                      />
                      {slugError && <p style={{ ...helperTextStyle, color: 'var(--v2-error)' }}>{slugError}</p>}
                    </div>
                  )}
                </div>

                <div style={{ borderTop: '1px solid var(--v2-border)', paddingTop: '24px' }}>
                  <div
                    style={{
                      backgroundColor: 'var(--v2-surface-alt)',
                      border: '1px solid var(--v2-border)',
                      borderRadius: 'var(--radius-md)',
                      padding: '16px',
                    }}
                  >
                    <p
                      style={{
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        color: 'var(--v2-text-muted)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        margin: '0 0 8px 0',
                      }}
                    >
                      Server Region
                    </p>
                    <p
                      style={{
                        fontSize: '1rem',
                        fontWeight: 600,
                        color: 'var(--v2-text)',
                        margin: '0 0 8px 0',
                      }}
                    >
                      {serverRegion === 'EU' ? '🇪🇺 Europe' : '🇺🇸 United States'}
                    </p>
                    <p style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)', margin: 0, lineHeight: 1.5 }}>
                      All workspaces deploy to your account's region. This cannot be changed per workspace.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 2: Select Plan */}
            {currentStep === 2 && (
<div>
                <p style={sectionTitleStyle}>{isSoftLaunch ? softLaunchMessaging.title : 'Choose Your Plan'}</p>
                {/* Soft Launch Banner */}
                {isSoftLaunch && (
                  <div style={{
                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                    border: '1px solid #fcd34d',
                    borderRadius: 'var(--radius-md)',
                    padding: '16px 20px',
                    marginBottom: '24px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <Gift size={20} strokeWidth={1.5} color="#d97706" style={{ flexShrink: 0, marginTop: '2px' }} />
                      <div>
                        <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#92400e', margin: '0 0 4px 0' }}>
                          {softLaunchMessaging.badge} — {trialDays} Days Free
                        </p>
                        <p style={{ fontSize: '0.8125rem', color: '#a16207', margin: 0, lineHeight: 1.5 }}>
                          {softLaunchMessaging.description}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {/* Access Code - hidden during soft launch */}
                {!isSoftLaunch && <div style={{ marginBottom: '24px' }}>
                  {!showAccessCodeInput ? (
                    <button
                      type="button"
                      onClick={() => setShowAccessCodeInput(true)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--v2-primary)',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    >
                      Have an access code?
                    </button>
                  ) : (
                    <div
                      style={{
                        backgroundColor: 'var(--v2-surface-alt)',
                        border: '1px solid var(--v2-border)',
                        borderRadius: 'var(--radius-md)',
                        padding: '14px 16px',
                      }}
                    >
                      <label style={labelStyle}>Access Code</label>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <input
                            type="text"
                            value={accessCode}
                            onChange={handleAccessCodeChange}
                            placeholder="Enter code"
                            style={{
                              ...inputStyle,
                              borderColor: accessCodeError
                                ? 'var(--v2-error)'
                                : accessCodeValidated
                                  ? 'var(--v2-success)'
                                  : 'var(--v2-border)',
                            }}
                          />
                          {(accessCodeError || accessCodeValidated) && (
                            <p
                              style={{
                                ...helperTextStyle,
                                color: accessCodeError ? 'var(--v2-error)' : 'var(--v2-success)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              {accessCodeError || (
                                <>
                                  <Check size={12} strokeWidth={2} /> Unlocked: {unlockedTier?.tier_name}
                                </>
                              )}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={handleAccessCodeApply}
                          disabled={!accessCode.trim() || accessCodeValidated}
                          style={{
                            padding: '10px 16px',
                            backgroundColor:
                              !accessCode.trim() || accessCodeValidated ? 'var(--v2-border)' : 'var(--v2-surface)',
                            color: !accessCode.trim() || accessCodeValidated ? 'var(--v2-text-muted)' : 'var(--v2-text)',
                            border: '1px solid var(--v2-border)',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            cursor: !accessCode.trim() || accessCodeValidated ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </div>}


                {unlockedTier && (
                  <div
                    style={{
                      backgroundColor: 'var(--v2-success-bg)',
                      border: '1px solid #86efac',
                      borderRadius: 'var(--radius-md)',
                      padding: '12px 16px',
                      marginBottom: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <PartyPopper size={18} strokeWidth={1.5} color="var(--v2-success-dark)" />
                    <p style={{ fontSize: '0.875rem', color: 'var(--v2-success-dark)', margin: 0 }}>
                      Special plan unlocked: <strong>{unlockedTier.tier_name}</strong>
                    </p>
                  </div>
                )}

                <div>
                  <label style={labelStyle}>Select a Plan *</label>
<TierSelectorCards
                    selectedTierId={selectedTier?.id}
                    onTierSelect={setSelectedTier}
                    filterCodes={['starter', 'growth', 'scale']}
                    unlockedTier={unlockedTier}
                    softLaunchMode={isSoftLaunch}
                    softLaunchDays={trialDays}
                    parityDiscountPct={(user as any)?.parity_discount_pct ?? null}
                    parityCountry={(user as any)?.parity_country ?? null}
                    campaign={myCampaign}
                  />
                </div>
              </div>
            )}

            {/* STEP 3: Admin & Billing */}
            {currentStep === 3 && (
              <div>
                <p style={sectionTitleStyle}>Billing & Admin Account</p>

                {/* Billing Profile */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={labelStyle}>Billing Account *</label>
                  {loadingProfiles ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Loader2 size={16} strokeWidth={2} color="var(--v2-primary)" style={{ animation: 'spin 1s linear infinite' }} />
                      <span style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)' }}>Loading billing accounts...</span>
                    </div>
                  ) : billingProfiles.length === 0 ? (
                    <div
                      style={{
                        backgroundColor: 'var(--v2-warning-bg)',
                        border: '1px solid #fcd34d',
                        borderRadius: 'var(--radius-md)',
                        padding: '12px 16px',
                      }}
                    >
                      <p style={{ fontSize: '0.875rem', color: '#92400e', margin: 0 }}>
                        No billing accounts found. Please create a billing account first.
                      </p>
                    </div>
                  ) : (
                    <div style={{ position: 'relative' }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setBillingDropdownOpen(!billingDropdownOpen);
                        }}
                        style={{
                          width: '100%',
                          padding: '12px 14px',
                          backgroundColor: 'var(--v2-surface)',
                          border: `1px solid ${billingDropdownOpen ? 'var(--v2-primary)' : 'var(--v2-border)'}`,
                          borderRadius: 'var(--radius-sm)',
                          fontSize: '0.875rem',
                          color: selectedBillingProfile ? 'var(--v2-text)' : 'var(--v2-text-muted)',
                          textAlign: 'left',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          boxShadow: billingDropdownOpen ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {selectedBillingProfile ? (
                            <>
                              {selectedBillingProfile.name}
                              {selectedBillingProfile.is_default && (
                                <Star size={12} strokeWidth={2} color="var(--v2-warning)" fill="var(--v2-warning)" />
                              )}
                            </>
                          ) : (
                            'Select a billing account'
                          )}
                        </span>
                        <ChevronDown
                          size={16}
                          strokeWidth={1.5}
                          color="var(--v2-text-muted)"
                          style={{
                            transition: 'transform 0.15s ease',
                            transform: billingDropdownOpen ? 'rotate(180deg)' : 'rotate(0)',
                          }}
                        />
                      </button>

                      {billingDropdownOpen && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            marginTop: '4px',
                            backgroundColor: 'var(--v2-surface)',
                            border: '1px solid var(--v2-border)',
                            borderRadius: 'var(--radius-md)',
                            boxShadow: 'var(--shadow-lg)',
                            zIndex: 10,
                            maxHeight: '200px',
                            overflowY: 'auto',
                            padding: '4px',
                          }}
                        >
                          {billingProfiles.map((profile) => (
                            <button
                              key={profile.id}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedBillingProfileId(profile.id);
                                setBillingDropdownOpen(false);
                              }}
                              style={{
                                width: '100%',
                                padding: '10px 12px',
                                backgroundColor:
                                  selectedBillingProfileId === profile.id ? 'var(--v2-primary-light)' : 'transparent',
                                border: 'none',
                                textAlign: 'left',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                borderRadius: 'var(--radius-sm)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                              }}
                              onMouseOver={(e) => {
                                if (selectedBillingProfileId !== profile.id) {
                                  e.currentTarget.style.backgroundColor = 'var(--v2-surface-hover)';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (selectedBillingProfileId !== profile.id) {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }
                              }}
                            >
                              <span style={{ fontWeight: 500, color: 'var(--v2-text)' }}>{profile.name}</span>
                              {profile.company_name && profile.company_name !== profile.name && (
                                <span style={{ color: 'var(--v2-text-tertiary)' }}>({profile.company_name})</span>
                              )}
                              {profile.is_default && (
                                <Star size={12} strokeWidth={2} color="var(--v2-warning)" fill="var(--v2-warning)" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div style={{ borderTop: '1px solid var(--v2-border)', paddingTop: '24px', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                    <label style={{ ...labelStyle, margin: 0 }}>Admin Account</label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={useMyInfo}
                        onChange={(e) => setUseMyInfo(e.target.checked)}
                        style={{ width: '16px', height: '16px', accentColor: 'var(--v2-primary)' }}
                      />
                      <span style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)' }}>Use my information</span>
                    </label>
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}>Admin Login or Email *</label>
                  <input
                    type="email"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    placeholder="admin@company.com"
                    disabled={useMyInfo}
                    style={{
                      ...inputStyle,
                      backgroundColor: useMyInfo ? 'var(--v2-bg)' : 'var(--v2-surface)',
                      color: useMyInfo ? 'var(--v2-text-tertiary)' : 'var(--v2-text)',
                      cursor: useMyInfo ? 'not-allowed' : 'text',
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                  <div>
                    <label style={labelStyle}>First Name *</label>
                    <input
                      type="text"
                      value={adminFirstName}
                      onChange={(e) => setAdminFirstName(e.target.value)}
                      placeholder="John"
                      disabled={useMyInfo}
                      style={{
                        ...inputStyle,
                        backgroundColor: useMyInfo ? 'var(--v2-bg)' : 'var(--v2-surface)',
                        color: useMyInfo ? 'var(--v2-text-tertiary)' : 'var(--v2-text)',
                        cursor: useMyInfo ? 'not-allowed' : 'text',
                      }}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Last Name *</label>
                    <input
                      type="text"
                      value={adminLastName}
                      onChange={(e) => setAdminLastName(e.target.value)}
                      placeholder="Doe"
                      disabled={useMyInfo}
                      style={{
                        ...inputStyle,
                        backgroundColor: useMyInfo ? 'var(--v2-bg)' : 'var(--v2-surface)',
                        color: useMyInfo ? 'var(--v2-text-tertiary)' : 'var(--v2-text)',
                        cursor: useMyInfo ? 'not-allowed' : 'text',
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>Password *</label>
                  <input
                    type="password"
                    value={adminPassword}
                    onChange={(e) => {
                      setAdminPassword(e.target.value);
                      setPasswordValidationError(null);
                    }}
                    placeholder={useMyInfo ? 'Enter your password to verify' : 'Enter admin password'}
                    style={{
                      ...inputStyle,
                      borderColor: passwordValidationError ? 'var(--v2-error)' : 'var(--v2-border)',
                    }}
                    onFocus={(e) => {
                      if (!passwordValidationError) {
                        e.target.style.borderColor = 'var(--v2-primary)';
                        e.target.style.boxShadow = '0 0 0 3px var(--v2-primary-muted)';
                      }
                    }}
onBlur={(e) => {
                      if (!passwordValidationError) {
                        e.target.style.borderColor = 'var(--v2-border)';
                      }
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  {!useMyInfo && (
                    <PasswordRequirements
                      password={adminPassword}
                      policy={passwordPolicy}
                      columns={1}
                    />
                  )}
<p
                    style={{
                      ...helperTextStyle,
                      color: passwordValidationError ? 'var(--v2-error)' : 'var(--v2-text-tertiary)',
                    }}
                  >
                    {passwordValidationError ||
		(useMyInfo ? 'Re-enter your account password to verify' : `Minimum ${passwordPolicy.minLength} characters`)}
                  </p>
                </div>

                {!useMyInfo && (
                  <div style={{ marginTop: '20px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notifyCustomerAdmin}
                        onChange={(e) => setNotifyCustomerAdmin(e.target.checked)}
                        style={{ width: '16px', height: '16px', accentColor: 'var(--v2-primary)' }}
                      />
                      <span style={{ fontSize: '0.875rem', color: 'var(--v2-text)' }}>
                        Send email notifications to client admin
                      </span>
                    </label>
                    {emailReadiness === 'none' && (
                      <p style={{ ...helperTextStyle, color: 'var(--v2-warning, #f59e0b)', marginTop: '6px' }}>
                        ⚠ No email sender configured. Your customer will receive emails from DashboardFox branding. Configure white-label email in Settings.
                      </p>
                    )}
                    {emailReadiness === 'partial' && (
                      <p style={{ ...helperTextStyle, color: 'var(--v2-warning, #f59e0b)', marginTop: '6px' }}>
                        ⚠ Custom sender set but not verified. Emails will use DashboardFox infrastructure.
                      </p>
                    )}
                    {emailReadiness === 'ready' && (
                      <p style={{ ...helperTextStyle, color: 'var(--v2-success, #10b981)', marginTop: '6px' }}>
                        ✓ White-label email configured. Emails will use your branding.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--v2-border)',
              backgroundColor: 'var(--v2-surface-alt)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <button
              onClick={onClose}
              onMouseEnter={() => setCancelHovered(true)}
              onMouseLeave={() => setCancelHovered(false)}
              style={{
                padding: '10px 20px',
                backgroundColor: cancelHovered ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
                color: cancelHovered ? 'var(--v2-text)' : 'var(--v2-text-tertiary)',
                border: '1px solid var(--v2-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              Cancel
            </button>

            <div style={{ display: 'flex', gap: '12px' }}>
              {currentStep > 1 && (
                <button
                  onClick={handleBack}
                  onMouseEnter={() => setBackHovered(true)}
                  onMouseLeave={() => setBackHovered(false)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: backHovered ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
                    color: 'var(--v2-text)',
                    border: '1px solid var(--v2-border)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.15s',
                  }}
                >
                  <ArrowLeft size={16} strokeWidth={1.5} />
                  Back
                </button>
              )}

              {currentStep < totalSteps ? (
                <button
                  onClick={handleNext}
                  disabled={!canProceed}
                  onMouseEnter={() => setNextHovered(true)}
                  onMouseLeave={() => setNextHovered(false)}
                  style={{
                    padding: '10px 24px',
                    backgroundColor: !canProceed
                      ? 'var(--v2-border)'
                      : nextHovered
                        ? 'var(--v2-primary-hover)'
                        : 'var(--v2-primary)',
                    color: !canProceed ? 'var(--v2-text-muted)' : '#ffffff',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: !canProceed ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleCreate}
                  disabled={!canCreate}
                  onMouseEnter={() => setNextHovered(true)}
                  onMouseLeave={() => setNextHovered(false)}
                  style={{
                    padding: '10px 24px',
                    backgroundColor: !canCreate
                      ? 'var(--v2-border)'
                      : nextHovered
                        ? 'var(--v2-primary-hover)'
                        : 'var(--v2-primary)',
                    color: !canCreate ? 'var(--v2-text-muted)' : '#ffffff',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: !canCreate ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s',
                    minWidth: '140px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                >
                  {(validatingPassword || creating) && (
                    <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                  )}
{validatingPassword ? 'Verifying...' : creating ? 'Creating...' : 'Create & Deploy'}
                </button>
              )}
            </div>
          </div>
          {currentStep === totalSteps && !canCreate && !creating && !validatingPassword && (
            <div style={{ padding: '0 24px 12px', textAlign: 'right' }}>
              <p style={{ fontSize: '0.75rem', color: 'var(--v2-text-tertiary)', margin: 0 }}>
                {(() => {
                  if (!isSoftLaunch && billingProfiles.length === 0) return 'Waiting for: a billing account';
                  if (!isSoftLaunch && !selectedBillingProfileId) return 'Waiting for: billing account selection';
                  if (!adminEmail?.trim()) return 'Waiting for: admin email';
                  if (!adminFirstName?.trim()) return 'Waiting for: admin first name';
                  if (!adminLastName?.trim()) return 'Waiting for: admin last name';
                  if (!adminPassword) return 'Waiting for: password';
if (!useMyInfo && Array.from(adminPassword).length < passwordPolicy.minLength) return `Waiting for: password (min ${passwordPolicy.minLength} chars)`;
                  return 'Waiting...';
                })()}
              </p>
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
};

export default CreateWorkspaceModal;
